/**
 * The rotation-day session: everything the scan screen needs to know, derived from the
 * day's setup plus the event log, and the events each action records.
 *
 * Pure and framework-free. The screen renders `deriveDay(...)` and appends what the
 * commands return; nothing here touches storage, the camera or React.
 */
import type { EventEnvelope } from "@hitl/core";
import {
  buildHistory,
  checkPlan,
  createAssessor,
  type Assessor,
  type CalendarDate,
  type Catalog,
  type DayPlan,
  type History,
  type PlanReport,
  type RepeatPolicy,
  type Route,
  type Verdict,
  type WriteOffReason,
} from "@hitl/rotations-domain";

export interface DaySetup {
  readonly catalog: Catalog;
  readonly route: Route;
  /** The plan she approved the night before (intent only). */
  readonly plan: DayPlan;
  readonly policy: RepeatPolicy;
  readonly historyKnownSince: CalendarDate;
}

export interface SpotView {
  readonly index: number;
  readonly slotId: string;
  readonly spotLabel: string;
  readonly clientId: string;
  readonly clientName: string;
  readonly plannedPotId: string | null;
  /** The pot standing here before today's swap (what she takes away). */
  readonly takesAway?: string;
  /** Where the taken-away pot goes next, per the plan; undefined means it comes home. */
  readonly takenAwayGoesTo?: { readonly index: number; readonly clientName: string; readonly spotLabel: string };
  /** Set once today's swap is recorded. */
  readonly done?: { readonly potId: string; readonly eventIds: readonly string[] };
}

export interface DayState {
  readonly history: History;
  readonly assessor: Assessor;
  readonly report: PlanReport;
  readonly spots: readonly SpotView[];
  /** First spot not yet done, or null when the day is done. */
  readonly nextOpenIndex: number | null;
}

/** An event before it is wrapped in an envelope by the log. */
export interface EventDraft {
  readonly type: "pot.placed" | "pot.collected" | "pot.rejected" | "pot.written-off" | "event.voided";
  readonly payload: Record<string, unknown>;
}

export function deriveDay(setup: DaySetup, events: readonly EventEnvelope[]): DayState {
  const { catalog, route, plan, policy, historyKnownSince } = setup;
  const history = buildHistory(events);
  const assessor = createAssessor({ catalog, history, policy, historyKnownSince });
  const today = route.on;

  // The plan was made against the morning's history: replay only events from before today.
  const morning = buildHistory(events.filter((e) => !isOn(e, today)));
  const report = checkPlan(plan, { catalog, history: morning, assessor: createAssessor({ catalog, history: morning, policy, historyKnownSince }) });

  const indexOfSlot = new Map(route.slotIds.map((id, i) => [id, i] as const));
  const todaysEventIds = (slotId: string): string[] =>
    events.filter((e) => isOn(e, today) && (e.payload as { slotId?: string }).slotId === slotId).map((e) => e.id);

  const spots: SpotView[] = route.slotIds.map((slotId, index) => {
    const slot = catalog.slot(slotId);
    const client = catalog.client(slot.clientId);
    const occupant = history.occupantOf(slotId);
    const morningOccupant = morning.occupantOf(slotId)?.potId;
    const handOn = report.handOns.find((h) => h.potId === morningOccupant && h.fromSlotId === slotId);
    const goesToIndex = handOn ? indexOfSlot.get(handOn.toSlotId) : undefined;
    const goesToSlot = handOn ? catalog.slot(handOn.toSlotId) : undefined;
    const doneToday = occupant && occupant.placedOn === today;

    return {
      index,
      slotId,
      spotLabel: slot.label,
      clientId: slot.clientId,
      clientName: client?.name ?? slot.clientId,
      plannedPotId: plan.swaps.find((s) => s.slotId === slotId)?.potId ?? null,
      ...(morningOccupant ? { takesAway: morningOccupant } : {}),
      ...(goesToIndex !== undefined && goesToSlot
        ? {
            takenAwayGoesTo: {
              index: goesToIndex,
              clientName: catalog.client(goesToSlot.clientId)?.name ?? goesToSlot.clientId,
              spotLabel: goesToSlot.label,
            },
          }
        : {}),
      ...(doneToday ? { done: { potId: occupant.potId, eventIds: todaysEventIds(slotId) } } : {}),
    };
  });

  const nextOpen = spots.find((s) => !s.done);
  return { history, assessor, report, spots, nextOpenIndex: nextOpen ? nextOpen.index : null };
}

function isOn(event: EventEnvelope, day: CalendarDate): boolean {
  return (event.payload as { on?: string } | null)?.on === day;
}

/** The verdict for a pot at a spot today. */
export function verdictAt(day: DayState, spot: SpotView, potId: string, on: CalendarDate): Verdict {
  return day.assessor.assess(potId, spot.slotId, on);
}

/**
 * Place a pot at a spot. Records the collection of the pot standing there (implied by the
 * swap) and the placement. Returns drafts in the order they happened.
 */
export function placeCommand(
  day: DayState,
  spot: SpotView,
  potId: string,
  on: CalendarDate,
  override?: string,
): EventDraft[] {
  const drafts: EventDraft[] = [];
  const standing = day.history.occupantOf(spot.slotId);
  if (standing && standing.potId !== potId) {
    drafts.push({
      type: "pot.collected",
      payload: { potId: standing.potId, clientId: spot.clientId, slotId: spot.slotId, on },
    });
  }
  drafts.push({
    type: "pot.placed",
    payload: {
      potId,
      clientId: spot.clientId,
      slotId: spot.slotId,
      on,
      ...(override ? { override: { reason: override } } : {}),
    },
  });
  return drafts;
}

export function rejectCommand(spot: SpotView, potId: string, on: CalendarDate, reason: string): EventDraft[] {
  return [
    {
      type: "pot.rejected",
      payload: { potId, clientId: spot.clientId, slotId: spot.slotId, on, ...(reason ? { reason } : {}) },
    },
  ];
}

/** Write a pot off (broken, deteriorated, lost): permanent evidence for the asset register. */
export function writeOffCommand(
  potId: string,
  on: CalendarDate,
  reason: WriteOffReason,
  extra: { note?: string; photoId?: string } = {},
): EventDraft[] {
  const note = extra.note?.trim();
  return [
    {
      type: "pot.written-off",
      payload: { potId, on, reason, ...(note ? { note } : {}), ...(extra.photoId ? { photoId: extra.photoId } : {}) },
    },
  ];
}

/** Undo = record a correction for each event; history is never edited. */
export function undoCommand(eventIds: readonly string[], reason = "undone on the phone"): EventDraft[] {
  return eventIds.map((id) => ({ type: "event.voided", payload: { voidsEventId: id, reason } }));
}

/** Today's spots each pot in hand is green/amber/red for: the ad-hoc "where can this go?" answer. */
export function whereCanItGo(
  day: DayState,
  potId: string,
  on: CalendarDate,
): { spot: SpotView; verdict: Verdict }[] {
  return day.spots.filter((s) => !s.done).map((spot) => ({ spot, verdict: day.assessor.assess(potId, spot.slotId, on) }));
}
